import { useMemo } from 'react';
import * as XLSX from 'xlsx';
import ValidationResultsTable from './ValidationResultsTable';
import { formatDate, formatAmount, getCountryName, extractRuleName } from '../../../utils/formatters';

interface ValidationResultsProps {
  evaluationResponse: any;
  thresholdMsg: string;
}

interface ExportRowData {
  'Alert ID': string;
  'Date': string;
  'Transaction Reference': string;
  'Rule Triggered': string;
  'Sender': string;
  'Receiver': string;
  'Country From': string;
  'Country To': string;
  'Amount': string;
  'Channel': string;
}

function ValidationResults({ evaluationResponse, thresholdMsg }: ValidationResultsProps): JSX.Element {
  // Danger banner when threshold message appears anywhere
  const hasThreshold = Array.isArray(evaluationResponse.results) && 
    evaluationResponse.results.some((r: any): boolean => {
      const notes = typeof r?.evaluationNotes === 'string' ? r.evaluationNotes.toLowerCase() : '';
      const msgs = Array.isArray(r?.validationMessages) ? r.validationMessages.join(' ').toLowerCase() : '';
      return notes.includes(thresholdMsg) || msgs.includes(thresholdMsg);
    });

  // Prepare table data for export (same logic as ValidationResultsTable)
  const tableDataForExport = useMemo(() => {
    const results = evaluationResponse.results || [];
    const payments = evaluationResponse.payments || [];
    
    if (results.length === 0) {
      return [];
    }

    // Create payment map
    const paymentMap = new Map<string, any>();
    payments.forEach((payment: any) => {
      if (payment.paymentId) {
        paymentMap.set(payment.paymentId, payment);
      }
      if (payment.endToEndId) {
        paymentMap.set(payment.endToEndId, payment);
      }
    });

    // Transform results into table rows (same logic as ValidationResultsTable)
    return results
      .map((result: any, index: number) => {
        const ruleTriggered = extractRuleName(result.validationMessages, result.issues);
        
        // Skip if no rule is triggered
        if (ruleTriggered === 'N/A') {
          return null;
        }

        let payment: any = null;
        if (result.payment) {
          payment = result.payment;
        } else {
          const resultIndex = result.index !== undefined ? result.index : index;
          if (payments.length > resultIndex) {
            payment = payments[resultIndex];
          }
          if (!payment && result.endToEndId) {
            payment = paymentMap.get(result.endToEndId);
            if (!payment) {
              payment = payments.find((p: any) => p.endToEndId === result.endToEndId || p.paymentId === result.endToEndId);
            }
          }
          if (!payment && result.paymentId) {
            payment = paymentMap.get(result.paymentId);
          }
          if (!payment && payments.length > index) {
            payment = payments[index];
          }
        }

        if (!payment) {
          payment = {};
        }

        // Generate alert ID in the same format as the table (A-YYYY-NNN)
        const year = new Date().getFullYear();
        const sequence = String(index + 1).padStart(3, '0');
        const alertId = `A-${year}-${sequence}`;
        const transactionReference = result.endToEndId || payment.endToEndId || payment.paymentId || result.paymentId || `payment_${index}`;
        const sender = result.payment?.debtorName || payment.debtorName || 'N/A';
        const receiver = result.payment?.creditorName || payment.creditorName || 'N/A';
        const countryFrom = getCountryName(result.payment?.debtorIban || payment.debtorIban);
        const countryTo = getCountryName(result.payment?.creditorIban || payment.creditorIban);
        
        let paymentAmount = result.payment?.amount ?? payment.amount;
        if (paymentAmount === undefined || paymentAmount === null) {
          paymentAmount = 0;
        }
        if (typeof paymentAmount === 'string') {
          paymentAmount = parseFloat(paymentAmount) || 0;
        }
        const amount = formatAmount(paymentAmount, result.payment?.currency || payment.currency || 'EUR');
        const channel = payment.type === 'SCT' ? 'SEPA Credit Transfer' : (payment.type || 'SEPA Credit Transfer');
        const date = payment.date ? formatDate(payment.date) : formatDate(new Date());

        return {
          'Alert ID': alertId,
          'Date': date,
          'Transaction Reference': transactionReference,
          'Rule Triggered': ruleTriggered,
          'Sender': sender,
          'Receiver': receiver,
          'Country From': countryFrom,
          'Country To': countryTo,
          'Amount': amount,
          'Channel': channel
        };
      })
      .filter((row: ExportRowData | null): row is ExportRowData => row !== null);
  }, [evaluationResponse.results, evaluationResponse.payments]);

  // Export to XLS function
  const handleExportToXLS = () => {
    if (tableDataForExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(tableDataForExport);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Validation Results');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `validation-results-${timestamp}.xlsx`;
    
    // Write file and trigger download
    XLSX.writeFile(wb, filename);
  };

  return (
    <section className="App-section">
      <div className="App-section-header">
        <h2 className="App-section-title">Alerts Results</h2>
        <p className="App-section-description">Review the alerts results and compliance analysis</p>
      </div>

      {/* Danger banner when threshold message appears anywhere */}
      {hasThreshold && (
        <div className="App-banner-danger">
          <div className="App-banner-icon">⚠️</div>
          <div className="App-banner-text">{thresholdMsg}</div>
        </div>
      )}

      {/* Validation Results Table */}
      {'results' in evaluationResponse && Array.isArray(evaluationResponse.results) && (
        <div className="App-payment-results">
          <div className="App-results-header">
            <h3 className="App-results-title">Alerts Results</h3>
            <button 
              className="App-download-button" 
              onClick={handleExportToXLS}
              disabled={tableDataForExport.length === 0}
              title="Download alerts results as Excel file"
            >
              <span className="App-download-icon">📥</span>
              Download XLS
            </button>
          </div>
          {(() => {
            const payments = evaluationResponse.payments || [];
            console.log('ValidationResults - evaluationResponse:', evaluationResponse);
            console.log('ValidationResults - payments array:', payments);
            console.log('ValidationResults - payments length:', payments.length);
            return (
              <ValidationResultsTable 
                results={evaluationResponse.results}
                payments={payments}
              />
            );
          })()}
        </div>
      )}
    </section>
  );
}

export default ValidationResults;

